/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import Lottie from 'react-lottie';
import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fillProfileInfo } from './store/reducers/user-profile';
import { normalizeProfileResponse } from './shared/profile';

// Services
import { AuthenticationController } from './services/authentication.services';

// Assets
import LoaderJson from './assets/json/loader.json';
import InternetDetector from './internet-detector';

// Components
const IntroPage = lazy(() => import('./views/IntroPage/IntroPage'));
const Profile = lazy(() => import('./views/Profile/Profile'));
const FAQ = lazy(() => import('./views/FAQ/FAQ'));
const Privacy = lazy(() => import('./views/Privacy/Privacy'));
const ProfileInfo = lazy(() => import('./views/ProfileInfo/ProfileInfo'));
const LoginRegister = lazy(() => import('./views/LoginRegister/LoginRegister'));
const HowToUse = lazy(() => import('./views/HowToUse/HowToUse'));
const CheckPicture = lazy(() => import('./views/CheckPicture/CheckPicture'));
const Result = lazy(() => import('./views/Result/Result'));
const TakePicture = lazy(() => import('./views/TakePicture/TakePicture'));
const ErrorPage = lazy(() => import('./views/ErrorPage/ErrorPage'));
const ForgetPassword = lazy(() => import('./views/ForgetPassword/ForgetPassword'));
const Possibilities = lazy(() => import('./views/Possibilities/Possibilities'));
const ChangePassword = lazy(() => import('./views/ChangePassword/ChangePassword'));

const RouterLoader = () => {
	const dispatch = useDispatch();
	const location = useLocation();

	useEffect(() => {
		if (!localStorage.getItem('profileInfo')) {
			if (
				window.location.pathname !== '/intro' &&
				window.location.pathname !== '/login-register' &&
				window.location.pathname !== '/forget-password'
			) {
				AuthenticationController.getProfileInfo().then(res => {
					const normalizedProfile = normalizeProfileResponse(res.data);
					dispatch(fillProfileInfo(normalizedProfile));
					localStorage.setItem('profileInfo', JSON.stringify(normalizedProfile));
				});
			}
		}
	}, [location.pathname]);

	return (
		<div className='loader_main_field_suspend'>
			<Lottie
				options={{
					loop: true,
					autoplay: true,
					animationData: LoaderJson,
					rendererSettings: {
						preserveAspectRatio: 'xMidYMid slice',
					},
				}}
				height={150}
				width={150}
			/>
		</div>
	);
};

export default function Router() {
	axios.interceptors.response.use(
		response => {
			return response;
		},
		error => {
			if (error?.response?.status === 401) {
				localStorage.clear();
				const responseURL = error?.request?.responseURL ?? '';
				const isAuthEndpoint =
					responseURL.includes('/api/users/login/') ||
					responseURL.includes('/api/users/sign-up/') ||
					responseURL.includes('/api/users/logout/');
				if (!isAuthEndpoint) {
					window.location.href = '/intro';
				}
			}
			return Promise.reject(error);
		},
	);

	return (
		<BrowserRouter>
			<main className='main_field_of_all_pages'>
				<Suspense fallback={<RouterLoader />}>
					<Routes>
						<Route
							path='/intro'
							element={
								<InternetDetector>
									<IntroPage />
								</InternetDetector>
							}
						/>
						<Route
							path='/privacy'
							element={
								<InternetDetector>
									<Privacy />
								</InternetDetector>
							}
						/>
						<Route
							path='/faq'
							element={
								<InternetDetector>
									<FAQ />
								</InternetDetector>
							}
						/>
						<Route
							path='/login-register'
							element={
								<InternetDetector>
									<LoginRegister />
								</InternetDetector>
							}
						/>
						<Route
							path='/profile-info'
							element={
								<InternetDetector>
									<ProfileInfo />
								</InternetDetector>
							}
						/>
						<Route
							path='/profile-info/change-password'
							element={
								<InternetDetector>
									<ChangePassword />
								</InternetDetector>
							}
						/>
						<Route
							path='/how-to-use'
							element={
								<InternetDetector>
									<HowToUse />
								</InternetDetector>
							}
						/>
						<Route
							path='/check-picture'
							element={
								<InternetDetector>
									<CheckPicture />
								</InternetDetector>
							}
						/>
						<Route
							path='/check-result'
							element={
								<InternetDetector>
									<Result />
								</InternetDetector>
							}
						/>
						<Route
							path='/take-picture'
							element={
								<InternetDetector>
									<TakePicture />
								</InternetDetector>
							}
						/>
						<Route
							path='/error-picture'
							element={
								<InternetDetector>
									<ErrorPage />
								</InternetDetector>
							}
						/>
						<Route
							path='/forget-password'
							element={
								<InternetDetector>
									<ForgetPassword />
								</InternetDetector>
							}
						/>
						<Route
							path='/possibilities'
							element={
								<InternetDetector>
									<Possibilities />
								</InternetDetector>
							}
						/>
						<Route
							path='/'
							element={
								<InternetDetector>
									<Profile />
								</InternetDetector>
							}
						/>
					</Routes>
				</Suspense>
			</main>
		</BrowserRouter>
	);
}
